<section class="reference-range-page" aria-live="polite">
  <mat-card class="form-card">
    <mat-card-header>
      <div mat-card-avatar class="icon-pill">
        <mat-icon>monitor_heart</mat-icon>
      </div>
      <mat-card-title>Observation Reference Ranges</mat-card-title>
      <mat-card-subtitle>
        Configure reusable ActivityDefinition templates so lab and service teams can reuse vetted ranges per test.
      </mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <form class="range-form" [formGroup]="form" (ngSubmit)="submitRange()">
        <div class="form-grid">
          <mat-form-field appearance="outline">
            <mat-label>Observation code</mat-label>
            <input type="text" matInput formControlName="code" [matAutocomplete]="codeAuto" [disabled]="loadingCodes">
            @if(codeControl.value) {
              <button mat-icon-button matSuffix type="button" aria-label="Clear selection" (click)="codeControl.setValue('')">
                <mat-icon>close</mat-icon>
              </button>
            }
            <mat-autocomplete #codeAuto="matAutocomplete" [displayWith]="displayCodeOption">
              @if(filteredCodes$ | async; as options) {
                @if(options.length === 0) {
                  <mat-option disabled>No matches</mat-option>
                } @else {
                  @for(option of options; track option.code) {
                    <mat-option [value]="option">
                      <div class="option-title">{{ option.display }}</div>
                      <div class="option-hint">{{ option.code }}</div>
                    </mat-option>
                  }
                }
              } @else {
                <mat-option disabled>Start typing to search</mat-option>
              }
            </mat-autocomplete>
            @if(codeControl.invalid && codeControl.touched) {
              <mat-error>Pick a code before saving.</mat-error>
            }
            <mat-hint>
              @if(loadingCodes) {
                Loading local catalogue…
              } @else {
                Powered by cached LOINC options.
              }
            </mat-hint>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Display override (optional)</mat-label>
            <input matInput formControlName="display" placeholder="e.g. Serum Potassium">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Reference range meaning</mat-label>
            <mat-select formControlName="type">
              @for(option of rangeTypeOptions; track option.value) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <div class="value-pair">
            <mat-form-field appearance="outline">
              <mat-label>Low value</mat-label>
              <input matInput type="number" formControlName="lowValue" placeholder="e.g. 3.5">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Low unit</mat-label>
              <input matInput formControlName="lowUnit" placeholder="mmol/L">
            </mat-form-field>
          </div>

          <div class="value-pair">
            <mat-form-field appearance="outline">
              <mat-label>High value</mat-label>
              <input matInput type="number" formControlName="highValue" placeholder="e.g. 5.2">
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>High unit</mat-label>
              <input matInput formControlName="highUnit" placeholder="mmol/L">
            </mat-form-field>
          </div>

          <mat-form-field appearance="outline" class="applies-to-field">
            <mat-label>Applies to</mat-label>
            <mat-select formControlName="appliesTo" multiple [compareWith]="compareConcepts">
              @for(option of appliesToOptions; track option.value) {
                <mat-option [value]="option.concept">{{ option.label }}</mat-option>
              }
            </mat-select>
            <mat-hint>Leave empty to apply to all patients.</mat-hint>
          </mat-form-field>

          <div class="age-group">
            <div class="age-field">
              <mat-form-field appearance="outline">
                <mat-label>Minimum age</mat-label>
                <input matInput type="number" formControlName="ageLowValue" placeholder="e.g. 18">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Unit</mat-label>
                <mat-select formControlName="ageLowUnit">
                  @for(option of ageUnitOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            <div class="age-field">
              <mat-form-field appearance="outline">
                <mat-label>Maximum age</mat-label>
                <input matInput type="number" formControlName="ageHighValue" placeholder="e.g. 65">
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Unit</mat-label>
                <mat-select formControlName="ageHighUnit">
                  @for(option of ageUnitOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
          </div>

          <mat-form-field appearance="outline" class="full-span">
            <mat-label>Notes / instructions</mat-label>
            <textarea matInput rows="3" formControlName="text" placeholder="Add narrative guidance or interpretation cues."></textarea>
          </mat-form-field>
        </div>

        <div class="actions">
          <button mat-stroked-button type="button" (click)="resetForm()" [disabled]="savingTemplate">
            Cancel
          </button>
          <span class="spacer"></span>
          <button mat-raised-button color="primary" type="submit" [disabled]="savingTemplate">
            @if(savingTemplate) {
              <mat-icon class="spin">hourglass_top</mat-icon>
            } @else {
              <mat-icon>save</mat-icon>
            }
            <span>{{ form.value.id ? 'Update Range' : 'Save Range' }}</span>
          </button>
        </div>
      </form>
    </mat-card-content>
  </mat-card>

  <mat-card>
    <mat-card-header>
      <mat-card-title>Saved templates</mat-card-title>
      <mat-card-subtitle>Latest ActivityDefinition resources synced from the FHIR server.</mat-card-subtitle>
      <span class="spacer"></span>
      <button mat-icon-button (click)="downloadTemplateCSV()" matTooltip="Download CSV template" color="primary">
        <mat-icon>download</mat-icon>
      </button>
      <button mat-icon-button (click)="fileInput.click()" matTooltip="Import from CSV" color="primary">
        <mat-icon>upload</mat-icon>
      </button>
      <input hidden #fileInput type="file" accept=".csv" (change)="onFileSelected($event)">
    </mat-card-header>
    <mat-card-content>
      @if(loadingTemplates) {
        <mat-progress-bar mode="indeterminate"></mat-progress-bar>
      } @else {
        @if(templates.length === 0) {
          <p class="empty-state">No reference ranges yet. Create one above to get started.</p>
        } @else {
          <div class="import-export-actions">
            <button mat-stroked-button size="small" (click)="downloadTemplatesAsCSV()" [disabled]="templates.length === 0">
              <mat-icon>file_download</mat-icon>
              <span>Export All as CSV</span>
            </button>
          </div>
          <table mat-table [dataSource]="templates" class="range-table">
            <ng-container matColumnDef="code">
              <th mat-header-cell *matHeaderCellDef>Observation</th>
              <td mat-cell *matCellDef="let template">
                <div class="code-cell">
                  <div class="primary">{{ template.display }}</div>
                  <div class="secondary">{{ template.code?.code }}</div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="range">
              <th mat-header-cell *matHeaderCellDef>Range</th>
              <td mat-cell *matCellDef="let template">{{ rangeSummary(template.range) }}</td>
            </ng-container>

            <ng-container matColumnDef="appliesTo">
              <th mat-header-cell *matHeaderCellDef>Applies To</th>
              <td mat-cell *matCellDef="let template">
                @if(template.range?.appliesTo?.length) {
                  <mat-chip-set>
                    @for(group of template.range?.appliesTo ?? []; track group.text ?? group.coding?.[0]?.code) {
                      <mat-chip>{{ group.text || group.coding?.[0]?.display || group.coding?.[0]?.code }}</mat-chip>
                    }
                  </mat-chip-set>
                } @else {
                  <span>All patients</span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="age">
              <th mat-header-cell *matHeaderCellDef>Age</th>
              <td mat-cell *matCellDef="let template">{{ ageSummary(template.range) }}</td>
            </ng-container>

            <ng-container matColumnDef="updated">
              <th mat-header-cell *matHeaderCellDef>Last Updated</th>
              <td mat-cell *matCellDef="let template">{{ template.lastUpdated | date:'medium' }}</td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let template">
                <button mat-icon-button color="primary" (click)="editTemplate(template)" matTooltip="Edit">
                  <mat-icon>edit</mat-icon>
                </button>
                <button mat-icon-button color="warn" (click)="deleteTemplate(template)" matTooltip="Delete">
                  <mat-icon>delete</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
          </table>
        }
      }
    </mat-card-content>
  </mat-card>
</section>
